import { APP_INITIALIZER, Optional } from '@angular/core';
import { InstanaLoggingBootstrapperConfig } from './instana-logging-bootstrapper.config';
import { INSTANA_EARLY_INIT_COMPLETE, IDENTITY_INIT_COMPLETE } from './instana-initialization.tokens';
import { InstanaLoggingBootstrapper } from './instana-logging.bootstrapper';
import { setupErrorHandlersImmediately } from './instana-error-handlers';
import { LIBRARY_NAME } from '../constants/library-name.constant';

/**
 * Gets the bootstrapper name lazily to avoid issues with class initialization.
 *
 * **Circular Dependency Scenario:**
 * This function is called during Angular module registration, which happens before
 * all classes are fully initialized. In some cases, there can be circular dependencies
 * between modules where:
 * - Module A imports Module B
 * - Module B imports Module A (directly or transitively)
 *
 * When this occurs, Angular's dependency injection system may attempt to access
 * `InstanaLoggingBootstrapper` before it has been fully initialized. Accessing static
 * properties or the class constructor at this point can throw a ReferenceError or
 * TypeError if the class is in an uninitialized state.
 *
 * **Why This Defensive Approach is Necessary:**
 * The try-catch block handles cases where:
 * 1. The class constructor hasn't been fully evaluated yet (ReferenceError)
 * 2. Static properties are accessed before class initialization completes (TypeError)
 * 3. The class is in a partially initialized state due to circular module dependencies
 *
 * By wrapping the access in a try-catch and providing a fallback, we ensure that
 * error handlers can be set up even when the bootstrapper class isn't fully ready,
 * preventing initialization failures that would block the entire application bootstrap.
 */
const getBootstrapperName = (): string => {
  try {
    if (InstanaLoggingBootstrapper?.NAME) {
      return InstanaLoggingBootstrapper.NAME;
    }
    if (InstanaLoggingBootstrapper?.name) {
      return `${LIBRARY_NAME}:${InstanaLoggingBootstrapper.name}`;
    }
  } catch {
    // Fallback if class is not yet initialized due to circular dependencies
    // This can occur when modules have circular imports and Angular's DI system
    // attempts to access the class before initialization completes
  }
  return `${LIBRARY_NAME}:InstanaLoggingBootstrapper`;
};

/**
 * Early initializer - loads Instana script and sets up global error handlers.
 * This MUST run FIRST, before ALL other initializers in the application.
 *
 * **CRITICAL:** Error handlers are set up immediately (synchronously) when this function
 * is called, ensuring they're active before any Angular code runs.
 *
 * **Two-Provider Pattern:**
 * This initializer uses a two-provider pattern to ensure proper execution:
 * 1. **Token Provider (INSTANA_EARLY_INIT_COMPLETE):** Creates and starts the initialization
 *    promise. Other initializers can depend on this token to wait for completion.
 * 2. **APP_INITIALIZER Provider:** Ensures initialization runs even if nothing depends on
 *    the token. Reuses the same promise from the token to avoid duplicate execution.
 *
 * **Why Both Are Needed:**
 * - Token alone: Initialization might not run if nothing depends on it
 * - APP_INITIALIZER alone: Other initializers can't depend on it for sequential execution
 * - Both together: Guarantees execution AND enables dependency injection
 *
 * **Execution Flow:**
 * 1. Error handlers set up synchronously (before Angular processes providers)
 * 2. Token provider factory runs → creates promise → starts initializeEarly()
 * 3. APP_INITIALIZER factory runs → receives promise → returns async function
 * 4. Angular calls APP_INITIALIZER functions → awaits promise → initialization completes
 *
 * @param config The Instana logging configuration
 */
export const InstanaLoggingEarlyInitializer = (config: InstanaLoggingBootstrapperConfig) => {
  // CRITICAL: Set up error handlers immediately, synchronously, before Angular processes providers
  // This happens during module registration, before any APP_INITIALIZER functions execute
  if (typeof window !== 'undefined') {
    setupErrorHandlersImmediately(undefined, getBootstrapperName());
  }

  return [
    InstanaLoggingBootstrapper,
    // Provider 1: Token that represents the initialization promise
    // This allows other initializers to depend on it for sequential execution
    {
      provide: INSTANA_EARLY_INIT_COMPLETE,
      useFactory: (initializer: InstanaLoggingBootstrapper) => {
        return initializer.initializeEarly(config);
      },
      deps: [InstanaLoggingBootstrapper],
    },
    // Provider 2: APP_INITIALIZER that ensures initialization runs
    // Depends on the token to reuse the same promise (avoids duplicate execution)
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: (earlyInstanaInitPromise: Promise<void>) => {
        // Return an async function that awaits the promise
        // This ensures the promise completes before Angular continues bootstrapping
        return async () => {
          await earlyInstanaInitPromise;
        };
      },
      deps: [INSTANA_EARLY_INIT_COMPLETE],
    }
  ];
};

/**
 * Late initializer - sets up user and route tracking.
 * This should run AFTER dependencies (IdentityService, RouteSourceService, PluginRouter) are ready.
 *
 * IMPORTANT: This depends on IDENTITY_INIT_COMPLETE token to ensure Identity initialization
 * completes before late Instana initialization runs.
 */
export const InstanaLoggingLateInitializer = (config: InstanaLoggingBootstrapperConfig) => {
  return [
    InstanaLoggingBootstrapper,
    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: (
        initializer: InstanaLoggingBootstrapper,
        identityInit?: Promise<void>
      ) => {
        return async () => {
          // Wait for Identity initialization to complete
          if (identityInit) {
            await identityInit;
          }
          return initializer.initializeLate(config);
        };
      },
      deps: [
        InstanaLoggingBootstrapper,
        [new Optional(), IDENTITY_INIT_COMPLETE]
      ],
    }
  ];
};

/**
 * Legacy initializer for backward compatibility - calls both phases sequentially.
 * @deprecated Use InstanaLoggingEarlyInitializer and InstanaLoggingLateInitializer separately instead.
 */
export const InstanaLoggingInitializer = (config: InstanaLoggingBootstrapperConfig) => ([
  InstanaLoggingBootstrapper,
  {
    provide: APP_INITIALIZER,
    multi: true,
    useFactory: (initializer: InstanaLoggingBootstrapper) => async () => initializer.initialize(config),
    deps: [InstanaLoggingBootstrapper],
  }
]);
